/**
 * One ceiling on decoded, cropped pixels held anywhere in the process.
 *
 * Cropped frames are the largest thing tracking holds, and more than one
 * consumer produces them: the player's session runtime, and the background
 * warmer working through videos that are not on screen. A budget owned by one
 * of them bounds only that one — the other's buffers are real memory the
 * ceiling never sees, and the more capable the device, the further apart the
 * intended and actual peaks drift.
 *
 * Reservations are taken before decoding and released when an interval's
 * buffers are done with, so the number tracks what is actually resident.
 */
const MIB = 1024 * 1024;

/** Matches `maxIntervalBytes`: one in-flight interval's worth of cropped pixels. */
const PER_SLOT_BYTES = 128 * MIB;

let budgetBytes = 0;
let reservedBytes = 0;

/** Sized once from what the device can carry, by the first consumer to ask. */
export function configurePixelBudget(computeSlots: number): void {
  budgetBytes = Math.max(budgetBytes, computeSlots * PER_SLOT_BYTES);
}

export function pixelBudgetBytes(): number {
  return budgetBytes;
}

export function reservedPixelBytes(): number {
  return reservedBytes;
}

/** True when the reservation fits; the caller must release exactly what it took. */
export function reservePixels(bytes: number): boolean {
  if (bytes <= 0 || bytes > budgetBytes) return false;
  if (reservedBytes + bytes > budgetBytes) return false;
  reservedBytes += bytes;
  return true;
}

export function releasePixels(bytes: number): void {
  reservedBytes = Math.max(0, reservedBytes - bytes);
}

/** Test seam: module state outlives any one player. */
export function resetPixelBudget(): void {
  budgetBytes = 0;
  reservedBytes = 0;
}
